import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { generatePOSummaryReport } from '../utils/reportUtils';


function Financials() {
  document.title = 'Financials';

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [northData, setNorthData] = useState([]);
  const [southData, setSouthData] = useState([]);
  const [kanbanData, setKanbanData] = useState([]);
  const [northStats, setNorthStats] = useState({ totalPOs: 0, totalBalance: 0, overdueJobs: 0 });
  const [southStats, setSouthStats] = useState({ totalPOs: 0, totalBalance: 0, overdueJobs: 0 });
  const [kanbanStats, setKanbanStats] = useState({ totalPOs: 0, totalBalance: 0 });
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('DATE DUE');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    const fetchData = async () => {
      if (authLoading) return;
      if (!user) {
        navigate('/login');
        return;
      }

      // Verify user role
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .single();

      if (userError || userData?.role !== 'admin') {
        console.error('Access denied:', userError?.message || 'User is not an admin');
        navigate('/access-denied');
        return;
      }

      setLoading(true);
      try {
        // Fetch all required data concurrently
        const [
          { data: jobs, error: jobsError },
          { data: kanbanJobs, error: kanbanJobsError },
          { data: deliveries, error: deliveriesError },
          { data: shops, error: shopsError },
          { data: partsData, error: partsError },
        ] = await Promise.all([
          supabase
            .from('jobs')
            .select(`
              id,
              shop_id,
              po_number,
              po_line_number,
              part_number,
              revision,
              description,
              expected_delivery_date,
              quantity_on_order,
              unit_price,
              status,
              shops (id, name)
            `)
            .is('kanban_job_id', null)
            .not('status', 'eq', 'closed')
            .not('expected_delivery_date', 'is', null),
          supabase
            .from('kanban_jobs')
            .select(`
              id,
              shop_id,
              po_number,
              po_line_number,
              part_number,
              description,
              quantity_on_order,
              unit_price,
              status,
              shops (id, name)
            `)
            .not('status', 'eq', 'closed'),
          supabase.from('deliveries').select('po_number, po_line_number, quantity'),
          supabase.from('shops').select('id, name'),
          supabase.from('parts').select('part_number, inventory_quantity'),
        ]);

        if (jobsError) throw new Error(`Error fetching jobs: ${jobsError.message}`);
        if (kanbanJobsError) throw new Error(`Error fetching kanban jobs: ${kanbanJobsError.message}`);
        if (deliveriesError) throw new Error(`Error fetching deliveries: ${deliveriesError.message}`);
        if (shopsError) throw new Error(`Error fetching shops: ${shopsError.message}`);
        if (partsError) throw new Error(`Error fetching parts: ${partsError.message}`);

        // Create lookup maps
        const inventoryMap = partsData.reduce((map, part) => {
          map[part.part_number] = part.inventory_quantity || 0;
          return map;
        }, {});

        const shopMap = shops.reduce((map, shop) => {
          map[shop.id] = shop.name || 'Unknown';
          return map;
        }, {});

        const shippedQuantities = deliveries.reduce((map, delivery) => {
          const key = `${delivery.po_number}-${delivery.po_line_number}`;
          map[key] = (map[key] || 0) + (delivery.quantity || 0);
          return map;
        }, {});

        const currentDate = new Date();
        const northDataTemp = [];
        const southDataTemp = [];
        const kanbanDataTemp = [];
        let northOverdueCount = 0;
        let southOverdueCount = 0;

        // Process jobs (regular and kanban)
        const processJob = (job, isKanban = false) => {
          if (job.po_number?.includes('TEST') || job.po_number?.includes('JAKE') || job.po_number?.includes('JUNK')) return null;

          const totalShipped = shippedQuantities[`${job.po_number}-${job.po_line_number}`] || 0;
          const orderQtyBalance = (job.quantity_on_order || 0) - totalShipped;
          if (orderQtyBalance <= 0) return null;

          const dollarBalance = orderQtyBalance * (job.unit_price || 0);
          const totalValue = (job.quantity_on_order || 0) * (job.unit_price || 0);
          const isOverdue = !isKanban && job.expected_delivery_date && new Date(job.expected_delivery_date) < currentDate;

          return {
            PO: job.po_number || '-',
            'PO Line #': job.po_line_number || '-',
            'PART#': job.part_number || '-',
            REVISION: isKanban ? '-' : job.revision || '-',
            DESCRIPTION: job.description || '-',
            'DATE DUE': isKanban ? 'KANBAN' : job.expected_delivery_date ? new Date(job.expected_delivery_date).toLocaleDateString() : '-',
            'QTY on Order': job.quantity_on_order || 0,
            PRICE: job.unit_price ? `$${job.unit_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
            'SHOP LOCATION': job.shops?.name || shopMap[job.shop_id] || '-',
            'TOTAL QTY SHIPPED': totalShipped,
            'ORDER QTY BALANCE': orderQtyBalance,
            '$ BALANCE': dollarBalance ? `$${dollarBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
            dollarBalanceValue: dollarBalance,
            '$ TOTAL': totalValue ? `$${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
            totalValue: totalValue,
            INVENTORY: inventoryMap[job.part_number] || 0,
            isOverdue,
          };
        };

        // Process regular jobs
        jobs.forEach(job => {
          const row = processJob(job);
          if (!row) return;
          if (job.shop_id === 1) {
            northDataTemp.push(row);
            if (row.isOverdue) northOverdueCount++;
          } else if (job.shop_id === 2) {
            southDataTemp.push(row);
            if (row.isOverdue) southOverdueCount++;
          }
        });

        // Process kanban jobs (separate table)
        kanbanJobs.forEach(job => {
          const row = processJob(job, true);
          if (!row) return;
          kanbanDataTemp.push(row);
        });

        // Sort by DATE DUE (ascending) by default
        const sortByDateDue = (data) => {
          return data.sort((a, b) => {
            const dateA = a['DATE DUE'] === 'KANBAN' ? new Date('9999-12-31') : new Date(a['DATE DUE']);
            const dateB = b['DATE DUE'] === 'KANBAN' ? new Date('9999-12-31') : new Date(b['DATE DUE']);
            return dateA - dateB;
          });
        };

        sortByDateDue(northDataTemp);
        sortByDateDue(southDataTemp);
        sortByDateDue(kanbanDataTemp);

        const countUniquePOs = (data) => {
            return new Set(data.map(row => row.PO)).size;
          };
          
          const northTotalPOs = countUniquePOs(northDataTemp);
          
          const southTotalPOs = countUniquePOs(southDataTemp);
          
          const kanbanTotalPOs = countUniquePOs(kanbanDataTemp);

        // Calculate stats
        const northTotalBalance = northDataTemp.reduce((sum, row) => sum + (row.dollarBalanceValue || 0), 0);
        const southTotalBalance = southDataTemp.reduce((sum, row) => sum + (row.dollarBalanceValue || 0), 0);
        const kanbanTotalBalance = kanbanDataTemp.reduce((sum, row) => sum + (row.dollarBalanceValue || 0), 0);

        setNorthData(northDataTemp);
        setSouthData(southDataTemp);
        setKanbanData(kanbanDataTemp);
        setNorthStats({ totalPOs: northTotalPOs, totalBalance: northTotalBalance, overdueJobs: northOverdueCount });
        setSouthStats({ totalPOs: southTotalPOs, totalBalance: southTotalBalance, overdueJobs: southOverdueCount });
        setKanbanStats({ totalPOs: kanbanTotalPOs, totalBalance: kanbanTotalBalance });
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, authLoading, navigate]);

  const handleSort = (field, shopData, setShopData) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);

    const sortedData = [...shopData].sort((a, b) => {
      let aValue = a[field] ?? '';
      let bValue = b[field] ?? '';
      if (field === 'DATE DUE') {
        const dateA = aValue === 'KANBAN' ? new Date('9999-12-31') : new Date(aValue);
        const dateB = bValue === 'KANBAN' ? new Date('9999-12-31') : new Date(bValue);
        return newDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }
      if (field === '$ BALANCE') {
        aValue = a.dollarBalanceValue || 0;
        bValue = b.dollarBalanceValue || 0;
      } else if (field === '$ TOTAL') {
        aValue = a.totalValue || 0;
        bValue = b.totalValue || 0;
      } else if (typeof aValue === 'number') {
        return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      aValue = typeof aValue === 'string' ? aValue : String(aValue);
      bValue = typeof bValue === 'string' ? bValue : String(bValue);
      return newDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });

    setShopData(sortedData);
  };

  const handleExportPDF = async () => {
    try {
      setLoading(true);
      
      // Call the utility with the data that's already been fetched
      await generatePOSummaryReport(
        northData,
        southData,
        kanbanData,
        northStats,
        southStats,
        kanbanStats
      );
      
      setLoading(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setLoading(false);
      // Optionally show an error message to the user
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  if (authLoading) return <div className="text-center text-gray-500 py-10">Loading authentication...</div>;
  if (loading) return <div className="text-center text-gray-500 py-10">Loading...</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="w-full mx-auto">
        
      <div className="flex justify-between items-center mb-6">
  <h1 className="text-3xl font-bold text-gray-900">Financials: PO Summary</h1>
  <button 
    onClick={handleExportPDF}
    disabled={loading}
    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition duration-150 disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center"
  >
    {loading ? (
      <>
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Generating...
      </>
    ) : (
      <>Export as PDF</>
    )}
  </button>
</div>

        {/* North Shop Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">North Shop</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white p-4 rounded-lg border-2">
              <h3 className="text-lg font-semibold text-gray-900">Individual POs</h3>
              <p className="text-2xl font-bold text-gray-700">{northStats.totalPOs}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border-2">
              <h3 className="text-lg font-semibold text-gray-900">Total $ Balance</h3>
              <p className="text-2xl font-bold text-gray-700">${northStats.totalBalance.toLocaleString()}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border-2">
              <h3 className="text-lg font-semibold text-gray-900">Overdue Line Items</h3>
              <p className="text-2xl font-bold text-gray-700">{northStats.overdueJobs}</p>
            </div>
          </div>
          {northData.length > 0 ? (
            <div className="bg-white rounded-lg border-2 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('PO', northData, setNorthData)}>
                      PO {sortField === 'PO' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('PO Line #', northData, setNorthData)}>
                      Line {sortField === 'PO Line #' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('PART#', northData, setNorthData)}>
                      PART# {sortField === 'PART#' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('DESCRIPTION', northData, setNorthData)}>
                      DESCRIPTION {sortField === 'DESCRIPTION' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('DATE DUE', northData, setNorthData)}>
                      DATE DUE {sortField === 'DATE DUE' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('QTY on Order', northData, setNorthData)}>
                      Order QTY {sortField === 'QTY on Order' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('TOTAL QTY SHIPPED', northData, setNorthData)}>
                      SHIPPED {sortField === 'TOTAL QTY SHIPPED' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('ORDER QTY BALANCE', northData, setNorthData)}>
                      BALANCE {sortField === 'ORDER QTY BALANCE' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('$ BALANCE', northData, setNorthData)}>
                      $ BALANCE {sortField === '$ BALANCE' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {northData.map((row, index) => (
                    <tr key={index} className={`hover:bg-gray-50 transition duration-150 ${row['DATE DUE'] !== 'KANBAN' && new Date(row['DATE DUE']) < new Date() ? 'bg-yellow-100' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.PO}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['PO Line #']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['PART#']}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 truncate max-w-xs" title={row.DESCRIPTION}>{row.DESCRIPTION.slice(0, 15) + '...'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['DATE DUE']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['QTY on Order']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['TOTAL QTY SHIPPED']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['ORDER QTY BALANCE']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['$ BALANCE']}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No data available for North Shop.</p>
          )}
        </div>

        {/* South Shop Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">South Shop</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white p-4 rounded-lg border-2">
              <h3 className="text-lg font-semibold text-gray-900">Individual POs</h3>
              <p className="text-2xl font-bold text-gray-700">{southStats.totalPOs}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border-2">
              <h3 className="text-lg font-semibold text-gray-900">Total $ Balance</h3>
              <p className="text-2xl font-bold text-gray-700">${southStats.totalBalance.toLocaleString()}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border-2">
              <h3 className="text-lg font-semibold text-gray-900">Overdue Line Items</h3>
              <p className="text-2xl font-bold text-gray-700">{southStats.overdueJobs}</p>
            </div>
          </div>
          {southData.length > 0 ? (
            <div className="bg-white rounded-lg border-2 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('PO', southData, setSouthData)}>
                      PO {sortField === 'PO' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('PO Line #', southData, setSouthData)}>
                      Line {sortField === 'PO Line #' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('PART#', southData, setSouthData)}>
                      PART# {sortField === 'PART#' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('DESCRIPTION', southData, setSouthData)}>
                      DESCRIPTION {sortField === 'DESCRIPTION' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('DATE DUE', southData, setSouthData)}>
                      DATE DUE {sortField === 'DATE DUE' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('QTY on Order', southData, setSouthData)}>
                      Order QTY {sortField === 'QTY on Order' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('TOTAL QTY SHIPPED', southData, setSouthData)}>
                      SHIPPED {sortField === 'TOTAL QTY SHIPPED' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('ORDER QTY BALANCE', southData, setSouthData)}>
                      BALANCE {sortField === 'ORDER QTY BALANCE' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('$ BALANCE', southData, setSouthData)}>
                      $ BALANCE {sortField === '$ BALANCE' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {southData.map((row, index) => (
                    <tr key={index} className={`hover:bg-gray-50 transition duration-150 ${row['DATE DUE'] !== 'KANBAN' && new Date(row['DATE DUE']) < new Date() ? 'bg-yellow-100' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.PO}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['PO Line #']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['PART#']}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 truncate max-w-xs" title={row.DESCRIPTION}>{row.DESCRIPTION.slice(0, 15) + '...'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['DATE DUE']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['QTY on Order']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['TOTAL QTY SHIPPED']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['ORDER QTY BALANCE']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['$ BALANCE']}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No data available for South Shop.</p>
          )}
        </div>

        {/* Kanban Section */}
        <div>
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kanban Jobs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-white p-4 rounded-lg border-2">
              <h3 className="text-lg font-semibold text-gray-900">Individual POs</h3>
              <p className="text-2xl font-bold text-gray-700">{kanbanStats.totalPOs}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border-2">
              <h3 className="text-lg font-semibold text-gray-900">Total $ Balance</h3>
              <p className="text-2xl font-bold text-gray-700">${kanbanStats.totalBalance.toLocaleString()}</p>
            </div>
          </div>
          {kanbanData.length > 0 ? (
            <div className="bg-white rounded-lg border-2 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('PO', kanbanData, setKanbanData)}>
                      PO {sortField === 'PO' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('PO Line #', kanbanData, setKanbanData)}>
                      Line {sortField === 'PO Line #' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('PART#', kanbanData, setKanbanData)}>
                      PART# {sortField === 'PART#' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('DESCRIPTION', kanbanData, setKanbanData)}>
                      DESCRIPTION {sortField === 'DESCRIPTION' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                   
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('QTY on Order', kanbanData, setKanbanData)}>
                      Order QTY {sortField === 'QTY on Order' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('TOTAL QTY SHIPPED', kanbanData, setKanbanData)}>
                      SHIPPED {sortField === 'TOTAL QTY SHIPPED' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('ORDER QTY BALANCE', kanbanData, setKanbanData)}>
                      BALANCE {sortField === 'ORDER QTY BALANCE' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('$ BALANCE', kanbanData, setKanbanData)}>
                      $ BALANCE {sortField === '$ BALANCE' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 inline" /> : <ChevronDownIcon className="w-4 h-4 inline" />)}
                    </th>
                    
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {kanbanData.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.PO}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['PO Line #']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['PART#']}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 truncate max-w-xs" title={row.DESCRIPTION}>{row.DESCRIPTION.slice(0, 15) + '...'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['QTY on Order']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['TOTAL QTY SHIPPED']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['ORDER QTY BALANCE']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['$ BALANCE']}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No data available for Kanban Jobs.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Financials;